import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { DollarSign, TrendingUp, TrendingDown, CheckCircle, Users, BarChart3, PieChart, Target } from 'lucide-react';
import { formatCurrency, channelNames } from './shared';

export const DashboardTab = ({ dashboard, conversionStats }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-[#C81D25] to-[#8B0000] text-white" data-testid="card-total-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm font-medium">Ingresos Totales</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(dashboard?.revenue?.total || 0)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
            <p className="text-white/60 text-xs mt-3">{dashboard?.memberships?.total || 0} membresías vendidas</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg" data-testid="card-month-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Ingresos Este Mes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{formatCurrency(dashboard?.revenue?.this_month || 0)}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${dashboard?.revenue?.growth_percentage >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {dashboard?.revenue?.growth_percentage >= 0 ? <TrendingUp className="w-6 h-6 text-emerald-600" /> : <TrendingDown className="w-6 h-6 text-red-600" />}
              </div>
            </div>
            <p className={`text-xs mt-3 font-medium ${dashboard?.revenue?.growth_percentage >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {dashboard?.revenue?.growth_percentage >= 0 ? '+' : ''}{dashboard?.revenue?.growth_percentage || 0}% vs mes anterior
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg" data-testid="card-active-memberships">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Membresías Activas</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboard?.memberships?.active || 0}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-gray-400 text-xs mt-3">{dashboard?.memberships?.pending || 0} pendientes de activación</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg" data-testid="card-total-users">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Clientes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{dashboard?.users?.total || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-gray-400 text-xs mt-3">{dashboard?.memberships?.this_month || 0} nuevos este mes</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#C81D25]" />Ventas Mensuales
            </CardTitle>
            <CardDescription>Últimos 6 meses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboard?.monthly_sales?.map((month) => {
                const maxSales = Math.max(...(dashboard?.monthly_sales?.map(m => m.sales) || [1]), 1);
                return (
                  <div key={month.month} className="flex items-center gap-4">
                    <span className="w-20 text-sm text-gray-500 shrink-0">{month.month}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                      <div className="bg-gradient-to-r from-[#C81D25] to-[#ff4d4d] h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                        style={{ width: `${Math.max(10, (month.sales / maxSales) * 100)}%` }}>
                        <span className="text-white text-xs font-bold">{month.sales}</span>
                      </div>
                    </div>
                    <span className="w-24 text-right text-sm font-semibold text-gray-700 shrink-0">{formatCurrency(month.revenue)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-[#C81D25]" />Ventas por Canal
            </CardTitle>
            <CardDescription>Distribución de ventas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(dashboard?.sales_by_channel || {}).map(([channel, count]) => {
                const colors = ['bg-[#C81D25]', 'bg-blue-500', 'bg-amber-500', 'bg-emerald-500', 'bg-purple-500'];
                const keys = Object.keys(dashboard?.sales_by_channel || {});
                const colorIdx = keys.indexOf(channel) % colors.length;
                const total = Object.values(dashboard?.sales_by_channel || {}).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                return (
                  <div key={channel} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${colors[colorIdx]}`} />
                      <span className="text-gray-700 font-medium">{channelNames[channel] || channel}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-500">{count} ventas</span>
                      <span className="font-bold text-gray-900 w-16 text-right">{percentage}%</span>
                    </div>
                  </div>
                );
              })}
              {Object.keys(dashboard?.sales_by_channel || {}).length === 0 && (
                <p className="text-gray-400 text-center py-8">No hay datos de ventas aún</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status overview */}
      <Card className="border-0 shadow-lg">
        <CardHeader><CardTitle className="text-lg">Estado de Membresías</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total', value: dashboard?.memberships?.total, bg: 'bg-gray-50', color: 'text-gray-900' },
              { label: 'Activas', value: dashboard?.memberships?.active, bg: 'bg-emerald-50', color: 'text-emerald-600' },
              { label: 'Pendientes', value: dashboard?.memberships?.pending, bg: 'bg-amber-50', color: 'text-amber-600' },
              { label: 'Consumidas', value: dashboard?.memberships?.consumed, bg: 'bg-slate-100', color: 'text-slate-600' },
              { label: 'Expiradas', value: dashboard?.memberships?.expired, bg: 'bg-red-50', color: 'text-red-600' },
            ].map((stat) => (
              <div key={stat.label} className={`text-center p-4 ${stat.bg} rounded-xl`}>
                <p className={`text-3xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                <p className={`text-sm ${stat.color}`}>{stat.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Conversion Funnel */}
      {conversionStats && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C81D25]" />Embudo de Conversión
            </CardTitle>
            <CardDescription>De registro a servicio utilizado</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { label: 'Registrados', value: conversionStats.funnel.registered, color: 'bg-blue-500', pct: 100 },
                { label: 'Compraron membresía', value: conversionStats.funnel.purchased, color: 'bg-amber-500', pct: conversionStats.funnel.registered > 0 ? (conversionStats.funnel.purchased / conversionStats.funnel.registered * 100) : 0 },
                { label: 'Membresía activa', value: conversionStats.funnel.active, color: 'bg-emerald-500', pct: conversionStats.funnel.registered > 0 ? (conversionStats.funnel.active / conversionStats.funnel.registered * 100) : 0 },
                { label: 'Servicio utilizado', value: conversionStats.funnel.consumed, color: 'bg-[#C81D25]', pct: conversionStats.funnel.registered > 0 ? (conversionStats.funnel.consumed / conversionStats.funnel.registered * 100) : 0 },
              ].map((step) => (
                <div key={step.label} className="flex items-center gap-4">
                  <span className="w-44 text-sm text-gray-600 shrink-0">{step.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-10 overflow-hidden">
                    <div className={`${step.color} h-full rounded-full flex items-center justify-end pr-3 transition-all duration-700`}
                      style={{ width: `${Math.max(8, step.pct)}%` }}>
                      <span className="text-white text-sm font-bold">{step.value}</span>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-500 w-14 text-right">{step.pct.toFixed(0)}%</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#C81D25]">{conversionStats.conversion_rate}%</p>
                <p className="text-xs text-gray-500">Tasa de conversión</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{conversionStats.prospects}</p>
                <p className="text-xs text-gray-500">Prospectos sin compra</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(conversionStats.total_revenue)}</p>
                <p className="text-xs text-gray-500">Ingresos totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
